import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AerialCanvas from "./AerialCanvas";
import AerialImagePickerModal from "./AerialImagePickerModal";
import AerialImageRequestModal from "./AerialImageRequestModal";
import AerialSidebar from "./AerialSidebar";
import { applyGeographicCoordinates, getMarkerImageContainment, projectMarkersToImage } from "./aerialCoordinates";
import { getVisibleAerialSections, mergeAerialSectionsWithProjectConditions } from "./aerialVisibility";

const resolveAerialImages = (aerialData) => {
	if (Array.isArray(aerialData?.images) && aerialData.images.length > 0) {
		return aerialData.images;
	}
	if (aerialData?.image) {
		return [aerialData.image];
	}
	return [];
};

const AerialView = ({
	api,
	token,
	projectId,
	aerialData,
	projectSections,
	selectedAerialDropboxId,
	onSelectAerialImage,
	focusedFieldId,
	aerialFocusRequestId,
	aerialSyncEpoch,
	stage,
	onMarkerChange,
	onMarkerRemove,
	onFocusField,
	onViewProjectField,
	onApplyMarkerSnapshot,
	onImageRequestCreated,
}) => {
	const [activeSidebarTab, setActiveSidebarTab] = useState("catalogue");
	const [selectedFieldId, setSelectedFieldId] = useState(null);
	const [placementFieldId, setPlacementFieldId] = useState(null);
	const [savingFieldIds, setSavingFieldIds] = useState([]);
	const [saveError, setSaveError] = useState("");
	const [staleAerialView, setStaleAerialView] = useState(false);
	const [staleBannerMessage, setStaleBannerMessage] = useState("Your aerial view was stale. It was synced with the latest markers.");
	const [showImagePicker, setShowImagePicker] = useState(false);
	const [showImageRequest, setShowImageRequest] = useState(false);
	const [imageRequestSubmitting, setImageRequestSubmitting] = useState(false);
	const [boundsWarningDismissed, setBoundsWarningDismissed] = useState(false);
	const [markerFocusRequestId, setMarkerFocusRequestId] = useState(0);
	const latestMarkersReference = useRef({});
	const markerSaveQueuesReference = useRef({});
	const pendingSaveCountsReference = useRef({});
	const deletedFieldIdsReference = useRef({});
	const skipNextFocusDetailReference = useRef(false);
	const lastFocusRequestIdReference = useRef(aerialFocusRequestId);
	const aerialViewVersionReference = useRef(aerialData.aerialViewVersion);
	const projectWriteQueueReference = useRef(Promise.resolve());
	const selectedImageReference = useRef(null);

	const aerialImages = useMemo(() => resolveAerialImages(aerialData), [aerialData]);
	const imageRequests = aerialData.imageRequests || [];
	const selectedImage =
		aerialImages.find((image) => image.dropboxId === selectedAerialDropboxId) || aerialData.image || aerialImages[0] || null;
	selectedImageReference.current = selectedImage;

	const fieldsById = useMemo(() => {
		const fieldMap = {};
		for (const section of aerialData.sections || []) {
			for (const field of section.fields || []) {
				fieldMap[field.id] = field;
			}
		}
		return fieldMap;
	}, [aerialData.sections]);

	const displayMarkers = useMemo(
		() => projectMarkersToImage(aerialData.markers || [], selectedImage),
		[aerialData.markers, selectedImage],
	);

	const markersByFieldId = useMemo(() => {
		const markerMap = {};
		for (const marker of displayMarkers) {
			markerMap[marker.fieldId] = marker;
		}
		return markerMap;
	}, [displayMarkers]);
	latestMarkersReference.current = markersByFieldId;

	const visibleSections = useMemo(
		() => getVisibleAerialSections(mergeAerialSectionsWithProjectConditions(aerialData.sections, projectSections)),
		[aerialData.sections, projectSections],
	);
	const visibleFieldIds = useMemo(() => {
		const fieldIds = new Set();
		for (const section of visibleSections) {
			for (const field of section.fields || []) {
				fieldIds.add(field.id);
			}
		}
		return fieldIds;
	}, [visibleSections]);
	const fieldIsVisible = useCallback(
		(fieldId) => {
			if (fieldId === undefined || fieldId === null) {
				return false;
			}
			if (visibleFieldIds.has(fieldId)) {
				return true;
			}
			const numericFieldId = Number(fieldId);
			return Number.isFinite(numericFieldId) && visibleFieldIds.has(numericFieldId);
		},
		[visibleFieldIds],
	);
	const visibleMarkers = displayMarkers.filter((marker) => fieldIsVisible(marker.fieldId));

	useEffect(() => {
		const nextVersion = Number(aerialData.aerialViewVersion);
		if (Number.isInteger(nextVersion)) {
			aerialViewVersionReference.current = nextVersion;
		}
		// Sync from GET/Refresh only. Write snapshots update the ref directly so marker-only state patches cannot clobber it.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aerialSyncEpoch]);

	useEffect(() => {
		if (!focusedFieldId || !fieldsById[focusedFieldId] || !fieldIsVisible(focusedFieldId)) {
			return;
		}
		setSelectedFieldId(focusedFieldId);
		setBoundsWarningDismissed(false);
		const isProjectFocusRequest = lastFocusRequestIdReference.current !== aerialFocusRequestId;
		lastFocusRequestIdReference.current = aerialFocusRequestId;
		if (isProjectFocusRequest) {
			skipNextFocusDetailReference.current = false;
			setActiveSidebarTab("detail");
			return;
		}
		if (skipNextFocusDetailReference.current) {
			skipNextFocusDetailReference.current = false;
			return;
		}
		setActiveSidebarTab("detail");
	}, [aerialFocusRequestId, fieldsById, fieldIsVisible, focusedFieldId, visibleFieldIds]);

	useEffect(() => {
		if (selectedFieldId && !fieldIsVisible(selectedFieldId)) {
			setSelectedFieldId(null);
		}
		if (placementFieldId && !fieldIsVisible(placementFieldId)) {
			setPlacementFieldId(null);
		}
	}, [fieldIsVisible, placementFieldId, selectedFieldId, visibleFieldIds]);

	const requestMarkerFocus = () => {
		setMarkerFocusRequestId((currentRequestId) => currentRequestId + 1);
	};

	const selectField = (fieldId, { openDetail = true, focusMarker = true } = {}) => {
		setSelectedFieldId(fieldId);
		setPlacementFieldId(null);
		skipNextFocusDetailReference.current = !openDetail;
		setBoundsWarningDismissed(false);
		onFocusField(fieldId);
		if (focusMarker) {
			requestMarkerFocus();
		}
		if (openDetail) {
			setActiveSidebarTab("detail");
		}
	};

	const mergeSnapshotMarkers = (serverMarkers, completingFieldId, rejected) => {
		const snapshotMarkers = Array.isArray(serverMarkers) ? serverMarkers : [];
		const mergedMarkers = snapshotMarkers.map((marker) => {
			const pendingCount = pendingSaveCountsReference.current[marker.fieldId] || 0;
			const stillInFlight = marker.fieldId === completingFieldId ? pendingCount > 1 : pendingCount > 0;
			if (!stillInFlight || (rejected && marker.fieldId === completingFieldId)) {
				return marker;
			}
			const localMarker = latestMarkersReference.current[marker.fieldId];
			if (!localMarker) {
				return marker;
			}
			return {
				...marker,
				relativeXCord: localMarker.relativeXCord,
				relativeYCord: localMarker.relativeYCord,
				lat: localMarker.lat ?? marker.lat,
				lon: localMarker.lon ?? marker.lon,
				note: localMarker.note ?? marker.note,
			};
		});
		const snapshotFieldIds = new Set(mergedMarkers.map((marker) => marker.fieldId));
		for (const [fieldId, pendingCount] of Object.entries(pendingSaveCountsReference.current)) {
			const numericFieldId = Number(fieldId);
			if (pendingCount <= 0 || snapshotFieldIds.has(numericFieldId) || deletedFieldIdsReference.current[numericFieldId]) {
				continue;
			}
			if (rejected && numericFieldId === completingFieldId) {
				continue;
			}
			const stillInFlight = numericFieldId === completingFieldId ? pendingCount > 1 : pendingCount > 0;
			if (stillInFlight && latestMarkersReference.current[numericFieldId]) {
				mergedMarkers.push(latestMarkersReference.current[numericFieldId]);
			}
		}
		return mergedMarkers;
	};

	const applyWriteSnapshot = (responseBody, { completingFieldId, rejected = false } = {}) => {
		const nextVersion = Number(responseBody?.aerialViewVersion);
		if (Number.isInteger(nextVersion)) {
			aerialViewVersionReference.current = nextVersion;
		}
		if (Array.isArray(responseBody?.markers)) {
			const mergedMarkers = mergeSnapshotMarkers(responseBody.markers, completingFieldId, rejected);
			const nextMarkersByFieldId = {};
			for (const marker of mergedMarkers) {
				nextMarkersByFieldId[marker.fieldId] = marker;
			}
			latestMarkersReference.current = nextMarkersByFieldId;
			onApplyMarkerSnapshot({
				markers: mergedMarkers,
				aerialViewVersion: Number.isInteger(nextVersion) ? nextVersion : undefined,
			});
		}
		if (rejected) {
			setStaleAerialView(true);
			setStaleBannerMessage(
				responseBody?.message || "Your change was not saved because your browser aerial view was outdated. It's now synced, no further action is required",
			);
			return;
		}
		if (responseBody?.stale) {
			setStaleAerialView(true);
			setStaleBannerMessage(
				responseBody?.message || "Your aerial view was stale. It's now synced, no further action is required.",
			);
			return;
		}
		setStaleAerialView(false);
	};

	const throwWriteError = (response, responseBody, fallbackMessage) => {
		const error = new Error(responseBody.message || fallbackMessage);
		error.status = response.status;
		error.stale = Boolean(responseBody.stale);
		throw error;
	};

	const enqueueProjectWrite = (writeFn) => {
		const queuedWrite = (projectWriteQueueReference.current || Promise.resolve())
			.catch(() => {
				// A failed write must not prevent the next requested update from running.
			})
			.then(writeFn);
		projectWriteQueueReference.current = queuedWrite;
		return queuedWrite;
	};

	const withGeographicCoordinates = (marker) => applyGeographicCoordinates(marker, selectedImageReference.current);

	const previewMarker = (marker) => {
		const nextMarker = withGeographicCoordinates(marker);
		latestMarkersReference.current = {
			...latestMarkersReference.current,
			[nextMarker.fieldId]: {
				...(latestMarkersReference.current[nextMarker.fieldId] || {}),
				...nextMarker,
			},
		};
		onMarkerChange(nextMarker);
	};

	const saveMarker = (marker, changedProperties = ["relativeXCord", "relativeYCord", "lat", "lon", "note"]) => {
		setSaveError("");
		const markerWithCoordinates =
			changedProperties.includes("relativeXCord") || changedProperties.includes("relativeYCord")
				? withGeographicCoordinates(marker)
				: marker;
		const propertiesToSave = [...changedProperties];
		if (changedProperties.includes("relativeXCord") || changedProperties.includes("relativeYCord")) {
			if (!propertiesToSave.includes("lat")) {
				propertiesToSave.push("lat");
			}
			if (!propertiesToSave.includes("lon")) {
				propertiesToSave.push("lon");
			}
		}
		const latestMarkerAtRequest = {
			...(latestMarkersReference.current[markerWithCoordinates.fieldId] || markerWithCoordinates),
		};
		for (const changedProperty of propertiesToSave) {
			latestMarkerAtRequest[changedProperty] = markerWithCoordinates[changedProperty];
		}
		latestMarkersReference.current = {
			...latestMarkersReference.current,
			[marker.fieldId]: latestMarkerAtRequest,
		};
		pendingSaveCountsReference.current[marker.fieldId] = (pendingSaveCountsReference.current[marker.fieldId] || 0) + 1;
		setSavingFieldIds((currentIds) => (currentIds.includes(marker.fieldId) ? currentIds : [...currentIds, marker.fieldId]));

		const previousSave = markerSaveQueuesReference.current[marker.fieldId] || Promise.resolve();
		const queuedSave = previousSave
			.catch(() => {
				// A failed save must not prevent the next requested update from running.
			})
			.then(() =>
				enqueueProjectWrite(async () => {
					if (deletedFieldIdsReference.current[marker.fieldId]) {
						return latestMarkersReference.current[marker.fieldId] || marker;
					}
					const latestMarker = latestMarkersReference.current[marker.fieldId] || markerWithCoordinates;
					const markerToSave = { ...latestMarker };
					for (const changedProperty of propertiesToSave) {
						markerToSave[changedProperty] = markerWithCoordinates[changedProperty];
					}

					const saveResponse = await fetch(`${api}/project/${projectId}/aerial-view/markers/${marker.fieldId}`, {
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
							Authorization: "Bearer " + token,
						},
						body: JSON.stringify({
							relativeXCord: markerToSave.relativeXCord,
							relativeYCord: markerToSave.relativeYCord,
							lat: markerToSave.lat ?? 0,
							lon: markerToSave.lon ?? 0,
							note: markerToSave.note || "",
							aerialViewVersion: aerialViewVersionReference.current,
						}),
					});

					const responseBody = await saveResponse.json();
					if (!saveResponse.ok) {
						if (saveResponse.status === 409 && responseBody.stale) {
							applyWriteSnapshot(responseBody, { completingFieldId: marker.fieldId, rejected: true });
						}
						throwWriteError(saveResponse, responseBody, "Unable to save marker.");
					}
					applyWriteSnapshot(responseBody, { completingFieldId: marker.fieldId });
					if (deletedFieldIdsReference.current[marker.fieldId]) {
						return responseBody.marker;
					}
					return latestMarkersReference.current[marker.fieldId] || responseBody.marker;
				}),
			)
			.catch((error) => {
				if (!error.stale && !deletedFieldIdsReference.current[marker.fieldId]) {
					setSaveError(error.message || "Unable to save marker.");
				}
				throw error;
			})
			.finally(() => {
				pendingSaveCountsReference.current[marker.fieldId] -= 1;
				if (pendingSaveCountsReference.current[marker.fieldId] <= 0) {
					delete pendingSaveCountsReference.current[marker.fieldId];
					setSavingFieldIds((currentIds) => currentIds.filter((fieldId) => fieldId !== marker.fieldId));
				}
			});

		markerSaveQueuesReference.current[marker.fieldId] = queuedSave;
		return queuedSave;
	};

	const removeMarkerFromState = (fieldId) => {
		const remainingMarkers = { ...latestMarkersReference.current };
		delete remainingMarkers[fieldId];
		latestMarkersReference.current = remainingMarkers;
		onMarkerRemove(fieldId);
	};

	const deleteMarker = (marker) => {
		setSaveError("");
		const fieldId = marker.fieldId;
		const markerToRestore = latestMarkersReference.current[fieldId] || marker;
		deletedFieldIdsReference.current[fieldId] = true;
		removeMarkerFromState(fieldId);
		pendingSaveCountsReference.current[fieldId] = (pendingSaveCountsReference.current[fieldId] || 0) + 1;
		setSavingFieldIds((currentIds) => (currentIds.includes(fieldId) ? currentIds : [...currentIds, fieldId]));

		const previousSave = markerSaveQueuesReference.current[fieldId] || Promise.resolve();
		const queuedDelete = previousSave
			.catch(() => {
				// A failed save must not prevent the requested delete from running.
			})
			.then(() =>
				enqueueProjectWrite(async () => {
					const deleteResponse = await fetch(`${api}/project/${projectId}/aerial-view/markers/${fieldId}`, {
						method: "DELETE",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
							Authorization: "Bearer " + token,
						},
						body: JSON.stringify({
							aerialViewVersion: aerialViewVersionReference.current,
						}),
					});
					let responseBody = {};
					try {
						responseBody = await deleteResponse.json();
					} catch (error) {
						responseBody = {};
					}
					if (!deleteResponse.ok) {
						if (deleteResponse.status === 409 && responseBody.stale) {
							delete deletedFieldIdsReference.current[fieldId];
							applyWriteSnapshot(responseBody, { completingFieldId: fieldId, rejected: true });
						}
						throwWriteError(deleteResponse, responseBody, "Unable to delete marker.");
					}
					applyWriteSnapshot(responseBody, { completingFieldId: fieldId });
				}),
			)
			.catch((error) => {
				if (error.stale) {
					throw error;
				}
				delete deletedFieldIdsReference.current[fieldId];
				if (!latestMarkersReference.current[fieldId]) {
					previewMarker(markerToRestore);
				}
				setSaveError(error.message || "Unable to delete marker.");
				throw error;
			})
			.finally(() => {
				pendingSaveCountsReference.current[fieldId] -= 1;
				if (pendingSaveCountsReference.current[fieldId] <= 0) {
					delete pendingSaveCountsReference.current[fieldId];
					setSavingFieldIds((currentIds) => currentIds.filter((id) => id !== fieldId));
				}
			});

		markerSaveQueuesReference.current[fieldId] = queuedDelete;
		return queuedDelete;
	};

	const submitImageRequest = async ({ lat, lon, scale }) => {
		setImageRequestSubmitting(true);
		try {
			const requestResponse = await fetch(`${api}/project/${projectId}/aerial-view/image-request`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + token,
				},
				body: JSON.stringify({ lat, lon, scale }),
			});
			const responseBody = await requestResponse.json();
			if (!requestResponse.ok) {
				throw new Error(responseBody.message || "Unable to submit the aerial image request.");
			}
			onImageRequestCreated(responseBody);
			setShowImageRequest(false);
		} finally {
			setImageRequestSubmitting(false);
		}
	};

	const placeMarker = async (fieldId, coordinates) => {
		delete deletedFieldIdsReference.current[fieldId];
		const existingMarker = latestMarkersReference.current[fieldId];
		const newMarker = withGeographicCoordinates({
			fieldId,
			relativeXCord: coordinates.relativeXCord,
			relativeYCord: coordinates.relativeYCord,
			lat: 0,
			lon: 0,
			note: existingMarker?.note || "",
		});
		previewMarker(newMarker);
		selectField(fieldId, { openDetail: true });
		try {
			await saveMarker(newMarker, ["relativeXCord", "relativeYCord", "lat", "lon"]);
		} catch (error) {
			// 409 snapshots already dropped the optimistic pin. Other errors leave it so the user can retry.
		}
	};

	const selectedField = selectedFieldId && fieldIsVisible(selectedFieldId) ? fieldsById[selectedFieldId] : null;
	const selectedMarker = selectedField ? markersByFieldId[selectedFieldId] : null;
	const selectedMarkerContainment = getMarkerImageContainment(selectedMarker, selectedImage);
	const markerOutsideImage = selectedMarkerContainment === "outside";
	const showBoundsWarning = markerOutsideImage && !boundsWarningDismissed;
	const readOnly = stage === "completed";
	const beginMarkerPlacement = (fieldId) => {
		setPlacementFieldId(fieldId);
		setActiveSidebarTab("detail");
	};

	return (
		<div className="bg-gray-200">
			{saveError ? (
				<div className="flex items-center justify-between bg-red-100 px-4 py-2 text-sm text-red-800">
					<span>{saveError}</span>
					<button type="button" className="font-semibold underline" onClick={() => setSaveError("")}>
						Dismiss
					</button>
				</div>
			) : null}
			{staleAerialView ? (
				<div className="flex items-center justify-between bg-amber-100 px-4 py-2 text-sm text-amber-900">
					<span>{staleBannerMessage}</span>
					<button type="button" className="font-semibold underline" onClick={() => setStaleAerialView(false)}>
						Dismiss
					</button>
				</div>
			) : null}
			{showBoundsWarning ? (
				<div className="flex items-center justify-between gap-3 bg-amber-100 px-4 py-2 text-sm text-amber-900">
					<span>This marker is outside the current aerial image. Switch to another larger aerial view or delete or reposition the marker.</span>
					<div className="flex shrink-0 items-center gap-3">
						<button type="button" className="font-semibold underline" onClick={() => setShowImagePicker(true)}>
							Select aerial image
						</button>
						{readOnly || !selectedFieldId || placementFieldId === selectedFieldId ? null : (
							<button
								type="button"
								className="font-semibold underline"
								onClick={() => beginMarkerPlacement(selectedFieldId)}>
								Relocate marker
							</button>
						)}
						<button type="button" className="font-semibold underline" onClick={() => setBoundsWarningDismissed(true)}>
							Dismiss
						</button>
					</div>
				</div>
			) : null}
			<div className="flex min-h-[60vh] flex-col md:flex-row">
				<AerialSidebar
					activeSidebarTab={activeSidebarTab}
					setActiveSidebarTab={setActiveSidebarTab}
					sections={visibleSections}
					markersByFieldId={markersByFieldId}
					selectedField={selectedField}
					selectedMarker={selectedMarker}
					markerOutsideImage={markerOutsideImage}
					placementActive={placementFieldId === selectedFieldId}
					readOnly={readOnly}
					saving={selectedFieldId ? savingFieldIds.includes(selectedFieldId) : false}
					onSelectField={(fieldId) => selectField(fieldId, { openDetail: false })}
					onBeginPlacement={beginMarkerPlacement}
					onCancelPlacement={() => setPlacementFieldId(null)}
					onPreviewMarker={previewMarker}
					onSaveMarker={(marker) => saveMarker(marker, ["note"])}
					onDeleteMarker={deleteMarker}
					onViewProjectField={onViewProjectField}
				/>
				<AerialCanvas
					image={selectedImage}
					markers={visibleMarkers}
					fieldsById={fieldsById}
					selectedFieldId={selectedFieldId}
					skipMarkerFocus={markerOutsideImage}
					aerialFocusRequestId={aerialFocusRequestId}
					markerFocusRequestId={markerFocusRequestId}
					expandedFieldId={activeSidebarTab === "detail" ? selectedFieldId : null}
					placementFieldId={placementFieldId}
					readOnly={readOnly}
					onSelectField={(fieldId) => selectField(fieldId, { openDetail: true, focusMarker: false })}
					onConfirmMarkerSelect={requestMarkerFocus}
					onPlaceMarker={placeMarker}
					onPreviewMarker={previewMarker}
					onSaveMarker={(marker) => saveMarker(marker, ["relativeXCord", "relativeYCord", "lat", "lon"])}
					onOpenImagePicker={() => setShowImagePicker(true)}
					onOpenImageRequest={() => setShowImageRequest(true)}
				/>
			</div>
			{showImagePicker ? (
				<AerialImagePickerModal
					images={aerialImages}
					imageRequests={imageRequests}
					selectedDropboxId={selectedImage?.dropboxId}
					onSelectImage={(dropboxId) => {
						onSelectAerialImage(dropboxId);
						setBoundsWarningDismissed(false);
						setShowImagePicker(false);
					}}
					onClose={() => setShowImagePicker(false)}
				/>
			) : null}
			{showImageRequest && !readOnly ? (
				<AerialImageRequestModal
					initialLat={selectedImage?.metadata?.originLat}
					initialLon={selectedImage?.metadata?.originLon}
					imageRequests={imageRequests}
					submitting={imageRequestSubmitting}
					onSubmit={submitImageRequest}
					onClose={() => setShowImageRequest(false)}
				/>
			) : null}
		</div>
	);
};

export default AerialView;
